import './payment-surfaces.css';
import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { locationApi, paymentApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { useAppConfig } from '../turnstile.tsx';
import { Alert, Button, Checkbox, LoadingBlock, TextInput, classes } from '../ui.tsx';
import { formatBasisPointsPercent, MAX_CARD_FEE_BASIS_POINTS } from '@shared/calculations/payments.ts';
import {
  paymentProviderDescriptor,
  type PaymentCredentialField,
  type PaymentProviderId,
} from '@shared/constants/payment-providers.ts';

/**
 * A typed rate to the basis points the API stores: "2.9" becomes 290. Clamped
 * here as well as server-side so the field cannot submit a value the API will
 * only bounce back.
 */
function percentToBasisPoints(value: string): number {
  const percent = Number(value);
  if (!Number.isFinite(percent)) return 0;
  return Math.min(MAX_CARD_FEE_BASIS_POINTS, Math.max(0, Math.round(percent * 100)));
}

/** The scope a settings read or write is aimed at: 'company', or a location id. */
const COMPANY_SCOPE = 'company';

/**
 * The card-processor connection for Settings.
 *
 * Each company bills through its OWN Stripe account. The recommended path is
 * Stripe Connect — a couple of clicks on stripe.com links the account, and
 * the app stores only its id. Where this deployment offers Connect, the card
 * leads with that: no webhook URL, no Copy URL, and no "signing secret not
 * set" warning, because Connect reports payment events on the platform
 * webhook. A company that already pasted a secret key (and no Connect
 * account) keeps that setup, including the endpoint it registered. Anyone
 * else can still open "Use API keys instead". A deployment with no Connect
 * configuration keeps the pasted-key card exactly as it was.
 *
 * Credentials are write-only: what comes back identifies the stored secret
 * (test/live, last four) and never repeats it, the same rule the app applies
 * to feed links and share tokens.
 *
 * Which boxes appear is driven entirely by the provider's descriptor in
 * `@shared/constants/payment-providers.ts` — the same table the API validates
 * against — so the form cannot ask for a field the server will refuse, or miss
 * one it requires.
 *
 * With two or more active locations a scope picker appears at the top: the
 * company account is the default every location inherits, and any location
 * can be given its own account instead — its payments then settle there. The
 * same form drives whichever scope is selected. A single-location company
 * never sees the picker — unless its one location has an account of its own,
 * which is the state a company lands in by archiving the others.
 *
 * `chargesCompletedVisits` is false on a plan without invoices (Free): its
 * card account takes payment links for one job at a time, and nothing there
 * bills a finished visit by itself, so "charge the card on file" has nothing
 * to switch and stays out of the card.
 */
export default function PaymentSettingsSection({
  chargesCompletedVisits = true,
}: {
  chargesCompletedVisits?: boolean;
}) {
  const { t } = useTranslation();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const appConfig = useAppConfig();

  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  // Only the extra fields the person has actually typed into. Untouched fields
  // stay out of the PATCH entirely, which is what lets one credential be
  // rotated without re-entering the rest.
  const [credentialEdits, setCredentialEdits] = useState<Record<string, string>>({});
  const [environmentChoice, setEnvironmentChoice] = useState<'test' | 'live' | null>(null);
  // Null means "not edited yet, show what the server has": the settings query
  // resolves after the first render, so seeding state from it would freeze the
  // fields at their pre-load defaults.
  const [cardFeePercent, setCardFeePercent] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);
  // "Use API keys instead", on a deployment that offers Connect. Closed until
  // the owner asks: the card leads with Connect, and the webhook URL is part
  // of the pasted-key path rather than the one they land on.
  const [manualKeysOpen, setManualKeysOpen] = useState(false);

  // Which account the form below is reading and writing: the company's, or
  // one location's own.
  const [scope, setScope] = useState<string>(COMPANY_SCOPE);

  const locationsQuery = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationApi.list(),
    enabled: can('company:read'),
  });
  const activeLocations = (locationsQuery.data?.locations ?? []).filter((location) => location.status === 'active');

  // One small read per active location, for the picker's "own account /
  // company account" note beside each entry — and for deciding whether the
  // picker appears at all. Keys match the main query below, so selecting a
  // scope reuses what the picker already fetched.
  const locationSummaries = useQueries({
    queries: activeLocations.map((location) => ({
      queryKey: ['payment-settings', location.id],
      queryFn: () => paymentApi.settings(location.id),
    })),
  });

  // A location that connected its own account keeps charging through it after
  // the company drops back to one active location — archiving the others, or
  // downgrading off Business. Hiding the picker there would leave the owner
  // editing an account nothing uses while the money settled somewhere they
  // could no longer see, so the picker outlives the second location.
  const someLocationHasOwnAccount = locationSummaries.some((summary) => summary.data?.configured === true);
  const showScopePicker = activeLocations.length >= 2 || someLocationHasOwnAccount;
  // A selected location that stops existing (archived under us) falls back to
  // the company scope rather than driving requests at a dead id.
  const effectiveScope =
    scope !== COMPANY_SCOPE && !activeLocations.some((location) => location.id === scope) ? COMPANY_SCOPE : scope;
  const locationScopeId = effectiveScope === COMPANY_SCOPE ? undefined : effectiveScope;

  const settings = useQuery({
    queryKey: ['payment-settings', effectiveScope],
    queryFn: () => paymentApi.settings(locationScopeId),
  });

  const canEdit = can('company:write');

  function selectScope(next: string) {
    if (next === scope) return;
    setScope(next);
    // Everything below the picker is per-scope state.
    setSecretKey('');
    setWebhookSecret('');
    setCredentialEdits({});
    setEnvironmentChoice(null);
    setCardFeePercent(null);
    setFieldErrors({});
    setMessage(null);
    setCopied(false);
    setManualKeysOpen(false);
  }

  function afterChange(text: string) {
    setSecretKey('');
    setWebhookSecret('');
    setCredentialEdits({});
    setEnvironmentChoice(null);
    setCardFeePercent(null);
    setFieldErrors({});
    setManualKeysOpen(false);
    setMessage({ tone: 'success', text });
    // Prefix match: the scope just written, the other scopes' picker notes,
    // and the resolved status all describe the same stored accounts.
    void queryClient.invalidateQueries({ queryKey: ['payment-settings'] });
    void queryClient.invalidateQueries({ queryKey: ['payments-status'] });
  }

  function onFailure(caught: unknown, fallback: string) {
    if (caught instanceof ApiRequestError) {
      setFieldErrors(caught.fields);
      setMessage(Object.keys(caught.fields).length > 0 ? null : { tone: 'error', text: caught.message });
    } else {
      setMessage({ tone: 'error', text: fallback });
    }
  }

  const save = useMutation({
    mutationFn: (body: {
      secretKey?: string;
      credentials?: Record<string, string>;
      webhookSecret?: string;
      environment?: 'test' | 'live';
      enabled?: boolean;
      cardFeeEnabled?: boolean;
      cardFeeBasisPoints?: number;
      autoChargeCardOnFile?: boolean;
    }) =>
      paymentApi.saveSettings({
        // Every write states the provider: the API treats a change of provider
        // as a full replacement, and a body that left it out would silently
        // save the new credentials against the old gateway.
        provider,
        ...body,
        ...(locationScopeId ? { locationId: locationScopeId } : {}),
      }),
    onSuccess: () => afterChange(t('paymentSettings.savedMessage')),
    onError: (caught) => onFailure(caught, t('paymentSettings.saveError')),
  });

  const disconnect = useMutation({
    mutationFn: () => paymentApi.disconnect(locationScopeId),
    onSuccess: () =>
      afterChange(
        locationScopeId ? t('paymentSettings.locationDisconnectedMessage') : t('paymentSettings.disconnectedMessage'),
      ),
    onError: (caught) => onFailure(caught, t('paymentSettings.disconnectError')),
  });

  // "Connect with Stripe": the API answers a stripe.com authorize URL and the
  // whole page leaves for it — the callback lands back on Settings with a
  // ?stripeConnect= flag the effect below words for a person.
  const connectStart = useMutation({
    mutationFn: () => paymentApi.connectStart(locationScopeId),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (caught) => onFailure(caught, t('paymentSettings.connectStartError')),
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('stripeConnect');
    if (!outcome) return;
    params.delete('stripeConnect');
    const query = params.toString();
    history.replaceState(
      history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );
    if (outcome === 'linked') {
      setMessage({ tone: 'success', text: t('paymentSettings.connectLinkedMessage') });
      void queryClient.invalidateQueries({ queryKey: ['payment-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['payments-status'] });
    } else if (outcome === 'declined') {
      setMessage({ tone: 'error', text: t('paymentSettings.connectDeclinedMessage') });
    } else if (outcome === 'in-use') {
      setMessage({ tone: 'error', text: t('paymentSettings.connectInUseMessage') });
    } else {
      setMessage({ tone: 'error', text: t('paymentSettings.connectErrorMessage') });
    }
    // Once, on mount: the flag is consumed off the URL it arrived on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);

    // Only fields belonging to the provider being saved.
    const credentials: Record<string, string> = {};
    for (const field of descriptor.extraFields) {
      const edited = credentialEdits[field.name];
      if (edited !== undefined) credentials[field.name] = edited.trim();
    }

    // Replacing a dead processor switches the account back on.
    //
    // `preparePaymentSettingsChanges` keeps the stored `enabled` when a write
    // does not mention it, so a retired row that was switched off stayed off:
    // the owner pastes their Stripe credentials, the save succeeds,
    // `getPaymentProvider` still answers null, and nothing is charged until
    // they find a second control that only appears after this one has been
    // used. The note above this form promises the opposite.
    //
    // Safe to assume because of WHAT is being replaced: the row named a
    // processor with no adapter, so its `enabled` flag was describing an
    // account that could not move money either way. Only when credentials are
    // actually being submitted — editing the card fee on a retired row is not
    // a reconnection.
    const replacingRetired = retiredProvider && (secretKey.trim() !== '' || Object.keys(credentials).length > 0);

    save.mutate({
      ...(secretKey.trim() ? { secretKey: secretKey.trim() } : {}),
      ...(Object.keys(credentials).length > 0 ? { credentials } : {}),
      ...(webhookSecret.trim() ? { webhookSecret: webhookSecret.trim() } : {}),
      ...(descriptor.needsEnvironmentChoice ? { environment } : {}),
      ...(cardFeePercent === null ? {} : { cardFeeBasisPoints: percentToBasisPoints(cardFeePercent) }),
      ...(replacingRetired ? { enabled: true } : {}),
    });
  }

  const data = settings.data;
  /*
    THE PROCESSOR, singular.

    There was a picker here, and for a while there was something to pick.
    There is not: `PAYMENT_PROVIDERS` names Stripe and nothing else, the
    Worker refuses a write naming anything else, and `providerOf` hands back
    null for a stored row that does. A select with one option in it is not a
    choice — it is a question whose answer the reader has to look up to
    discover there was never more than one.

    The adapter seam under it stays exactly as it is. It is not there to offer
    a choice: it is there so that everything the app knows about moving money
    is one interface, card details never touch this Worker, and a second
    processor would be a new module rather than a rewrite. None of that
    depends on a dropdown.
  */
  const provider: PaymentProviderId = 'stripe';
  const descriptor = paymentProviderDescriptor(provider);
  // Stripe Connect: linking is the recommended path wherever the deployment
  // offers it (`/config` → `stripeConnectAvailable`, set from
  // STRIPE_CONNECT_CLIENT_ID). A linked row has no credentials to type.
  const connectAvailable = appConfig.data?.stripeConnectAvailable ?? false;
  const linkedAccount = data?.stripeConnectAccountId ?? null;
  // A settings row naming a processor this build cannot drive — one a
  // deployment offered before. It is already dead: `providerOf` returns null
  // for it, so nothing can be charged or refunded through that account. The
  // form says so by treating it as unconnected and asking for Stripe's
  // credentials, rather than showing a connected account that moves no money.
  //
  // Read from `unsupportedProvider` rather than by comparing `provider`: the
  // serializer resolves `provider` to something this build can drive, so the
  // comparison was true for no row that exists and this whole branch was
  // unreachable.
  const retiredProvider = Boolean(data?.configured) && Boolean(data?.unsupportedProvider);
  /**
   * A pasted secret key and no Connect account. That company registered — or
   * still has to register — a webhook on its own Stripe account, so the
   * endpoint and the signing-secret line stay. A Connect link stores an
   * account id instead of a key, and Stripe reports its events on the
   * platform webhook.
   */
  const usingManualKeys = Boolean(data?.secretKeyLast4) && !linkedAccount && !retiredProvider;
  /**
   * Connect is offered, and this scope is not already on a pasted key or a
   * retired processor (whose recovery is still the credential form). The
   * manual fields stay behind "Use API keys instead" until the owner opens
   * them. A deployment without Connect never takes this branch.
   */
  const manualKeysOptIn = connectAvailable && !linkedAccount && !usingManualKeys && !retiredProvider;
  const showCredentialInputs = !linkedAccount && (!manualKeysOptIn || manualKeysOpen);
  /** Endpoint URL, Copy URL, and the signing-secret field — the pasted-key path. */
  const showManualWebhook = showCredentialInputs && !retiredProvider && Boolean(descriptor.webhookField);
  /**
   * "Secret key" / "Webhook signing secret: Not set" in the summary. Hidden
   * while Connect is the path they have not opted out of, so opening the
   * disclosure does not put that warning back above the Connect button. A
   * company already on a pasted key still sees whether its secret is set.
   */
  const showManualKeySummary = !retiredProvider && !linkedAccount && (!connectAvailable || usingManualKeys);
  /**
   * ...and whether that dead row is the one the money actually uses.
   *
   * Two different questions, and only the first one is about the form. A
   * LOCATION row is skipped by `findEffectivePaymentSettings` unless it is
   * `enabled` AND holds a key, so a branch whose retired row is switched off
   * charges and refunds through the company's Stripe account like any other —
   * telling its owner that nothing can be charged is the same false claim as
   * the one this PR fixes, pointed the other way. A COMPANY row has nothing
   * to fall back to, so it stays retired whether it is on or off, which is
   * what stops somebody switching a dead account back on expecting it to work.
   *
   * The form keeps reading `retiredProvider`: the recovery is identical
   * either way, and it is Stripe credentials.
   */
  const retiredInForce = retiredProvider && (data?.enabled === true || data?.scope === 'company');
  const environment: 'test' | 'live' =
    environmentChoice ?? (retiredProvider ? 'test' : ((data?.keyMode as 'test' | 'live' | null) ?? 'test'));

  /**
   * A credential's label and hint, translated where a locale has strings for
   * them and falling back to the descriptor's English. The descriptor is the
   * source of truth for WHICH fields exist; the locale files only dress them.
   */
  function fieldLabel(field: PaymentCredentialField): string {
    return t(`paymentSettings.fields.${field.name}.label`, { defaultValue: field.label });
  }
  function fieldHint(field: PaymentCredentialField): string {
    return t(`paymentSettings.fields.${field.name}.hint`, { defaultValue: field.hint });
  }
  // Each scope's account has its own webhook endpoint, absolute and resolved
  // server-side — the company URL, or this location's own.
  const webhookUrl = data?.webhookUrl ?? '';

  async function copyWebhookUrl() {
    if (!webhookUrl) return;
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setMessage({ tone: 'error', text: t('paymentSettings.copyError') });
    }
  }

  return (
    <section className="card work-section payment-leaf mb-6" aria-labelledby="payments-heading">
      <div className="work-section-heading">
        <h2 id="payments-heading" className="mb-3 text-lg font-semibold">
          {t('paymentSettings.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">
        {connectAvailable && !usingManualKeys && !retiredProvider
          ? t('paymentSettings.descriptionConnect')
          : t('paymentSettings.description')}
      </p>

      {showScopePicker ? (
        <fieldset className="mb-4">
          <legend className="field-label">{t('paymentSettings.scopeLabel')}</legend>
          <div className="grid gap-2">
            <label
              className={classes(
                'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                effectiveScope === COMPANY_SCOPE
                  ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
                  : 'border-[color:var(--color-line)]',
              )}
            >
              <input
                type="radio"
                name="payment-scope"
                className="mt-1"
                checked={effectiveScope === COMPANY_SCOPE}
                onChange={() => selectScope(COMPANY_SCOPE)}
              />
              <span>
                <span className="block font-semibold">{t('paymentSettings.scopeCompany')}</span>
                <span className="field-hint">{t('paymentSettings.scopeCompanyHint')}</span>
              </span>
            </label>
            {activeLocations.map((location, index) => {
              const summary = locationSummaries[index]?.data;
              return (
                <label
                  key={location.id}
                  className={classes(
                    'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                    effectiveScope === location.id
                      ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
                      : 'border-[color:var(--color-line)]',
                  )}
                >
                  <input
                    type="radio"
                    name="payment-scope"
                    className="mt-1"
                    checked={effectiveScope === location.id}
                    onChange={() => selectScope(location.id)}
                  />
                  <span>
                    <span className="block font-semibold">{location.name}</span>
                    <span className="field-hint">
                      {/* A branch whose row names a processor this build cannot
                          drive is not "connected": nothing can be charged or
                          refunded through it. It stays in the list — the
                          recovery is inside it — but it says what it is.

                          First, though, whether the row is used at all:
                          `findEffectivePaymentSettings` takes a location row
                          only when it is enabled AND holds a key, and falls
                          back to the company otherwise. So a switched-off row
                          — retired or perfectly good — describes a branch that
                          charges through the company account, and the line
                          mirrors that rule rather than reading `configured`
                          alone. */}
                      {summary
                        ? !(summary.enabled && summary.configured)
                          ? t('paymentSettings.scopeUsesCompany')
                          : summary.unsupportedProvider
                            ? t('paymentSettings.scopeRetiredProvider')
                            : t('paymentSettings.scopeOwnAccount')
                        : t('common.loading')}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      {locationScopeId ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('paymentSettings.locationScopeNote')}</p>
      ) : null}

      {settings.isLoading ? <LoadingBlock label={t('paymentSettings.loading')} /> : null}

      {message ? (
        <div className="mb-4">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}

      {data ? (
        <>
          <dl className="mb-4 grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-semibold">{t('paymentSettings.providerLabel')}</dt>
              <dd className="text-[color:var(--color-muted)]">
                {/* What the row NAMES, when this build cannot drive it. The
                    resolved `provider` would say Stripe here, which is the one
                    answer that cannot be right: the money is not moving
                    through Stripe and it is not moving through anything. */}
                {data.unsupportedProvider ?? paymentProviderDescriptor(provider).name}
              </dd>
            </div>
            <div>
              <dt className="font-semibold">{t('paymentSettings.statusLabel')}</dt>
              <dd className="text-[color:var(--color-muted)]">
                {/* And the status says so before `configured`/`enabled` get a
                    word in. Both are true of a retired row and both are
                    misleading: nothing can be charged or refunded through it,
                    which is the opposite of what "connected, on" means. This
                    line is outside the edit form, so it is also the only
                    warning a reader without `company:write` ever sees. */}
                {retiredInForce
                  ? t('paymentSettings.statusRetiredProvider')
                  : retiredProvider
                    ? // Retired, but switched off on a branch — so the company
                      // account is what serves it, and it serves it properly.
                      // Its own string rather than the plain inherits one,
                      // which would claim the branch has no account of its
                      // own: it has a dead one, and that is worth knowing
                      // before anybody switches it back on.
                      t('paymentSettings.statusRetiredProviderOff')
                    : !data.configured
                      ? locationScopeId
                        ? t('paymentSettings.statusLocationInherits')
                        : t('paymentSettings.statusNotConnected')
                      : !data.enabled
                        ? t('paymentSettings.statusConnectedOff')
                        : t('paymentSettings.statusConnectedOn', {
                            mode:
                              data.keyMode === 'live' ? t('paymentSettings.modeLive') : t('paymentSettings.modeTest'),
                          })}
              </dd>
            </div>
            {/* Everything from here to the card fee describes the CONNECTED
                account, in the vocabulary of the processor driving it. None of
                it is true of a retired row: the credential it would label with
                Stripe's field name belongs to another processor entirely, and
                a webhook line for an account that moves no money is setup
                advice for a thing that cannot be set up. The recovery is in
                the form below, and for a reader who cannot edit, the status
                line above is the whole answer. */}
            {retiredProvider ? null : linkedAccount ? (
              <div>
                <dt className="font-semibold">{t('paymentSettings.connectAccountLabel')}</dt>
                <dd className="text-[color:var(--color-muted)] break-all">{linkedAccount}</dd>
              </div>
            ) : showManualKeySummary ? (
              <div>
                <dt className="font-semibold">{fieldLabel(descriptor.secretField)}</dt>
                <dd className="text-[color:var(--color-muted)]">
                  {data.secretKeyLast4 ? `•••• ${data.secretKeyLast4}` : t('paymentSettings.secretKeyNotSet')}
                </dd>
              </div>
            ) : null}
            {retiredProvider
              ? null
              : descriptor.extraFields.map((field) => (
                  <div key={field.name}>
                    <dt className="font-semibold">{fieldLabel(field)}</dt>
                    <dd className="text-[color:var(--color-muted)]">
                      {/* Non-secret fields come back in full; secret ones only say
                          that something is stored. */}
                      {data.credentials?.[field.name] ??
                        (data.credentialsConfigured?.includes(field.name)
                          ? t('paymentSettings.credentialStored')
                          : t('paymentSettings.secretKeyNotSet'))}
                    </dd>
                  </div>
                ))}
            {showManualKeySummary && descriptor.webhookField ? (
              <div>
                <dt className="font-semibold">{t('paymentSettings.webhookSecretLabel')}</dt>
                <dd className="text-[color:var(--color-muted)]">
                  {data.webhookConfigured ? t('paymentSettings.webhookSet') : t('paymentSettings.webhookNotSet')}
                </dd>
              </div>
            ) : null}
            <div>
              <dt className="font-semibold">{t('paymentSettings.cardFeeLabel')}</dt>
              <dd className="text-[color:var(--color-muted)]">
                {data.cardFeeEnabled && data.cardFeeBasisPoints > 0
                  ? t('paymentSettings.cardFeeOn', { percent: formatBasisPointsPercent(data.cardFeeBasisPoints) })
                  : t('paymentSettings.cardFeeOff')}
              </dd>
            </div>
            {/* In the summary as well as the form: whether completed cleanings
                charge themselves is the kind of thing somebody who cannot edit
                these settings still needs to be able to look up. */}
            {chargesCompletedVisits ? (
              <div>
                <dt className="font-semibold">{t('paymentSettings.autoChargeSummaryLabel')}</dt>
                <dd className="text-[color:var(--color-muted)]">
                  {data.autoChargeCardOnFile ? t('paymentSettings.autoChargeOn') : t('paymentSettings.autoChargeOff')}
                </dd>
              </div>
            ) : null}
          </dl>

          {/* Connect leads. The webhook essay used to sit above this button
              even when the deployment had nothing for the owner to register
              — Connect reports events itself. A pasted-key company still
              sees the endpoint, below, because that URL is theirs. */}
          {canEdit && connectAvailable ? (
            <div className="mb-4">
              {linkedAccount ? (
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    busy={connectStart.isPending}
                    onClick={() => connectStart.mutate()}
                  >
                    {t('paymentSettings.connectRelink')}
                  </Button>
                  <p className="field-hint m-0">{t('paymentSettings.connectRelinkHint')}</p>
                </div>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="primary"
                    busy={connectStart.isPending}
                    onClick={() => connectStart.mutate()}
                  >
                    {t('paymentSettings.connectWithStripe')}
                  </Button>
                  <p className="field-hint mt-2">{t('paymentSettings.connectWithStripeHint')}</p>
                </>
              )}
            </div>
          ) : null}

          {canEdit && manualKeysOptIn ? (
            <div className="mb-4">
              <Button
                type="button"
                variant="ghost"
                aria-expanded={manualKeysOpen}
                onClick={() => setManualKeysOpen((open) => !open)}
              >
                {t('paymentSettings.useApiKeysInstead')}
              </Button>
              {manualKeysOpen ? null : <p className="field-hint mt-2">{t('paymentSettings.useApiKeysInsteadHint')}</p>}
            </div>
          ) : null}

          {/* Telling somebody to register this endpoint in Stripe, for
              Stripe's event types, is advice about an account they do not
              have — a retired processor, or a Connect link, or a company
              that has not chosen pasted keys. See the summary rows above. */}
          {retiredProvider ? null : linkedAccount ? (
            <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('paymentSettings.connectNoWebhookNote')}</p>
          ) : showManualWebhook ? (
            <div className="mb-4">
              <p className="mb-1 text-sm font-semibold">{t('paymentSettings.webhookEndpointLabel')}</p>
              <p className="mb-2 text-sm text-[color:var(--color-muted)]">
                <Trans
                  i18nKey={
                    locationScopeId
                      ? 'paymentSettings.webhookEndpointDescriptionLocation'
                      : 'paymentSettings.webhookEndpointDescription'
                  }
                  components={[
                    <code key="0" />,
                    <code key="1" />,
                    <code key="2" />,
                    <code key="3" />,
                    <code key="4" />,
                    <code key="5" />,
                  ]}
                />
              </p>
              <p className="mb-2 break-all font-mono text-xs">{webhookUrl}</p>
              <Button type="button" onClick={() => void copyWebhookUrl()}>
                {copied ? t('common.copied') : t('paymentSettings.copyUrl')}
              </Button>
            </div>
          ) : descriptor.webhookField ? null : (
            <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('paymentSettings.noWebhookNote')}</p>
          )}
        </>
      ) : null}

      {canEdit && data && (showCredentialInputs || data.configured) ? (
        <form onSubmit={handleSubmit} noValidate>
          {showCredentialInputs ? (
            <>
              <p className="field-label">{t('paymentSettings.providerFieldLabel')}</p>
              <p className="mb-1 font-semibold">{descriptor.name}</p>
              <p className="field-hint mb-3">{descriptor.description}</p>

              {retiredProvider ? (
                <div className="mb-4">
                  {/* The recovery is the same either way — Stripe credentials
                      — but the stakes are not. A branch whose dead row is
                      switched off is taking payments perfectly well through
                      the company account, so the warning wording would send
                      its owner hunting for a fault that is not there; what
                      they need to know is that switching it back on is what
                      would break it. */}
                  <Alert tone={retiredInForce ? 'warning' : 'info'}>
                    {t(
                      retiredInForce ? 'paymentSettings.retiredProviderNote' : 'paymentSettings.retiredProviderNoteOff',
                      {
                        provider: descriptor.name,
                      },
                    )}
                  </Alert>
                </div>
              ) : null}

              <TextInput
                label={fieldLabel(descriptor.secretField)}
                type="password"
                autoComplete="off"
                placeholder={
                  data.configured && !retiredProvider
                    ? t('paymentSettings.secretKeyPlaceholderKeep')
                    : t('paymentSettings.secretKeyPlaceholderNew')
                }
                hint={fieldHint(descriptor.secretField)}
                value={secretKey}
                error={fieldErrors.secretKey}
                onChange={(event) => setSecretKey(event.target.value)}
              />

              {descriptor.extraFields.map((field) => {
                const stored = retiredProvider ? undefined : data.credentials?.[field.name];
                const isStored = !retiredProvider && data.credentialsConfigured?.includes(field.name);
                return (
                  <TextInput
                    key={field.name}
                    label={fieldLabel(field)}
                    // Secret extras are write-only like the primary secret; the
                    // rest are ordinary text so an owner can see what is stored.
                    type={field.secret ? 'password' : 'text'}
                    autoComplete="off"
                    placeholder={
                      field.secret && isStored
                        ? t('paymentSettings.secretKeyPlaceholderKeep')
                        : t('paymentSettings.credentialOptional')
                    }
                    hint={fieldHint(field)}
                    value={credentialEdits[field.name] ?? (field.secret ? '' : (stored ?? ''))}
                    error={fieldErrors[`credentials.${field.name}`]}
                    onChange={(event) =>
                      setCredentialEdits((current) => ({ ...current, [field.name]: event.target.value }))
                    }
                  />
                );
              })}

              {descriptor.needsEnvironmentChoice ? (
                <>
                  <label className="field-label" htmlFor="payment-environment">
                    {t('paymentSettings.environmentLabel')}
                  </label>
                  <select
                    id="payment-environment"
                    className="field-control"
                    value={environment}
                    onChange={(event) => setEnvironmentChoice(event.target.value as 'test' | 'live')}
                  >
                    <option value="test">{t('paymentSettings.environmentTest')}</option>
                    <option value="live">{t('paymentSettings.environmentLive')}</option>
                  </select>
                  <p className="field-hint mb-3">{t('paymentSettings.environmentHint')}</p>
                </>
              ) : null}

              {descriptor.webhookField ? (
                <TextInput
                  label={fieldLabel(descriptor.webhookField)}
                  type="password"
                  autoComplete="off"
                  placeholder={
                    data.webhookConfigured && !retiredProvider
                      ? t('paymentSettings.webhookPlaceholderKeep')
                      : t('paymentSettings.webhookPlaceholderNew')
                  }
                  hint={fieldHint(descriptor.webhookField)}
                  value={webhookSecret}
                  error={fieldErrors.webhookSecret}
                  onChange={(event) => setWebhookSecret(event.target.value)}
                />
              ) : null}
            </>
          ) : null}

          {data.configured && !retiredProvider ? (
            <>
              <Checkbox
                label={t('paymentSettings.enabledLabel')}
                checked={data.enabled}
                onChange={(event) => {
                  setMessage(null);
                  save.mutate({ enabled: event.target.checked });
                }}
              />
              {/*
                Money moving with nobody watching, so the hint says all three
                things an owner needs before ticking it: when it fires, that a
                customer without a saved card is untouched by it, and that a
                schedule already set to bill automatically keeps doing so
                whether this is on or off.
              */}
              {chargesCompletedVisits ? (
                <Checkbox
                  label={t('paymentSettings.autoChargeLabel')}
                  hint={t('paymentSettings.autoChargeHint')}
                  checked={data.autoChargeCardOnFile}
                  onChange={(event) => {
                    setMessage(null);
                    save.mutate({ autoChargeCardOnFile: event.target.checked });
                  }}
                />
              ) : null}
              <Checkbox
                label={t('paymentSettings.cardFeeEnabledLabel')}
                hint={t('paymentSettings.cardFeeEnabledHint')}
                checked={data.cardFeeEnabled}
                onChange={(event) => {
                  setMessage(null);
                  save.mutate({
                    cardFeeEnabled: event.target.checked,
                    // Switching it on with nothing typed yet would store 0% and
                    // charge nobody anything, so the field's value goes with it.
                    ...(cardFeePercent === null ? {} : { cardFeeBasisPoints: percentToBasisPoints(cardFeePercent) }),
                  });
                }}
              />
              {data.cardFeeEnabled ? (
                <div className="grid gap-x-4 sm:grid-cols-2">
                  <TextInput
                    label={t('paymentSettings.cardFeePercentLabel')}
                    type="number"
                    min={0}
                    max={MAX_CARD_FEE_BASIS_POINTS / 100}
                    step={0.01}
                    hint={t('paymentSettings.cardFeePercentHint')}
                    // No fee stored yet shows as an empty box with "0" behind
                    // it, rather than a 0 that has to be deleted before a rate
                    // can be typed.
                    placeholder="0"
                    value={
                      cardFeePercent ?? (data.cardFeeBasisPoints === 0 ? '' : String(data.cardFeeBasisPoints / 100))
                    }
                    error={fieldErrors.cardFeeBasisPoints}
                    onChange={(event) => setCardFeePercent(event.target.value)}
                  />
                </div>
              ) : null}
            </>
          ) : null}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="submit" variant="primary" busy={save.isPending}>
              {data.configured && !retiredProvider
                ? t('paymentSettings.savePaymentSettings')
                : t('paymentSettings.connectProvider', { provider: descriptor.name })}
            </Button>
            {data.configured ? (
              <Button type="button" variant="secondary" busy={disconnect.isPending} onClick={() => disconnect.mutate()}>
                {locationScopeId ? t('paymentSettings.disconnectLocation') : t('paymentSettings.disconnect')}
              </Button>
            ) : null}
          </div>
        </form>
      ) : null}

      {!canEdit && data ? (
        <p className="text-sm text-[color:var(--color-muted)]">{t('paymentSettings.ownerOnly')}</p>
      ) : null}
    </section>
  );
}
